import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import { titleCase } from "@/lib/tool-kit/citation-sources-more/common";
import {
  dayMonthYear,
  get,
  italic,
  type LegalValues,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  aglcSpan,
  bracketedParagraphs,
  invertName,
  pageList,
  readAuthorList,
  shortenSpansIn,
  surnameOf,
} from "@/lib/tool-kit/legal-styles/common";

/*
 * Australian cases, legislation, journal articles, books and Hansard in AGLC4 footnote
 * form, with the subsequent reference and the bibliography entry.
 *
 * Source: the Australian Guide to Legal Citation, 4th edition (Melbourne University Law
 * Review Association in collaboration with the Melbourne Journal of International Law,
 * 2018), the free PDF from Melbourne Law School (law.unimelb.edu.au/mulr/aglc), read in
 * October 2026. At that date AGLC5 was still in consultation and unpublished, so AGLC4
 * is the current edition. Rules used, by number:
 * - 1.1.4 footnotes end with a full stop; 1.1.6-1.1.7 pinpoints follow the citation,
 *   page then [paragraph], spans of pages shortened by 1.10.1 but spans of paragraphs
 *   written "[50]-[59]"; 1.4.1 subsequent references "Short Title (n 53) pinpoint";
 *   1.4.3 "Ibid" capitalised at the start of a footnote, an unchanged pinpoint not
 *   repeated; 1.4.4 short titles in single quotation marks and brackets, after pinpoints
 *   and parentheses; 1.7 capitalisation of titles; 1.10.1 spans; 1.11.1 "15 June 1985";
 *   1.13 bibliography entries ("an author's first name and surname should be inverted",
 *   no full stop).
 * - 2.1 case names in italics (first plaintiff and first defendant, surnames only);
 *   2.1.15 the name is omitted from the first footnote when the sentence gives it; 2.2.1
 *   "(year)" for volume-numbered series, "[year]" for year-organised series; 2.2.2 the
 *   authorised report is cited in preference, 2.2.7 no parallel citations; 2.2.5 reported
 *   pinpoints follow a comma; 2.3.1 medium neutral citations, "[2017] HCA 4, [57]";
 *   2.3.2 unreported cases without one, "(Supreme Court of Victoria, Hampel J, 29 June
 *   1989) 3"; 2.4.1 the judge in brackets after the pinpoint.
 * - 3.1 Acts: title and year in italics, jurisdiction in roman in brackets, pinpoint
 *   abbreviations (s, ss, sch, pt, div, cl, reg, r); 3.4 delegated legislation cited the
 *   same way; 3.5 short title.
 * - 4.1 authors (initials closed up, no full stops, up to three named, then "et al"),
 *   4.2 titles; 5 journal articles "Author, 'Title' (2013) 37(1) Melbourne University Law
 *   Review 46, 50"; 6 books "Author, Title (Publisher, 2nd ed, 2016) 31"; 7.5.1
 *   Hansard "Commonwealth, Parliamentary Debates, Senate, 7 February 2017, 39 (George
 *   Brandis, Attorney-General)".
 *
 * The page examples are checked against these functions in
 * `src/lib/__tests__/legal-styles-aglc.test.ts`.
 */

export type AglcKind = "case" | "legislation" | "journal" | "book" | "hansard";

export const AGLC_FIELDS = {
  caseName: "caseName",
  report: "report",
  neutral: "neutral",
  unreported: "unreported",
  pages: "pages",
  paragraphs: "paragraphs",
  judge: "judge",
  court: "court",
  inText: "inText",
  shortTitle: "shortTitle",
  noteNumber: "noteNumber",
  title: "title",
  jurisdiction: "jurisdiction",
  parts: "parts",
  authors: "authors",
  body: "body",
  workTitle: "workTitle",
  yearStyle: "yearStyle",
  year: "year",
  volume: "volume",
  issue: "issue",
  journal: "journal",
  startPage: "startPage",
  pinpoint: "pinpoint",
  publisher: "publisher",
  edition: "edition",
  hJurisdiction: "hJurisdiction",
  chamber: "chamber",
  date: "date",
  speaker: "speaker",
} as const;

const F = AGLC_FIELDS;

export interface AglcLabels {
  aglc: string;
  footnote: string;
  later: string;
  ibid: string;
  bibliography: string;
  caseNote: string;
  caseBothNote: string;
  legislationNote: string;
  journalNote: string;
  bookNote: string;
  hansardNote: string;
}

/* ------------------------------------------------------------------ helpers */

const WORD_ABBREVIATIONS: readonly [RegExp, string][] = [
  [/\bsub-?sections\b/gi, "sub-ss"],
  [/\bsub-?section\b/gi, "sub-s"],
  [/\bsections\b/gi, "ss"],
  [/\bsection\b/gi, "s"],
  [/\bschedules\b/gi, "schs"],
  [/\bschedule\b/gi, "sch"],
  [/\bdivisions\b/gi, "divs"],
  [/\bdivision\b/gi, "div"],
  [/\bparts\b/gi, "pts"],
  [/\bpart\b/gi, "pt"],
  [/\bregulations\b/gi, "regs"],
  [/\bregulation\b/gi, "reg"],
  [/\brules\b/gi, "rr"],
  [/\brule\b/gi, "r"],
  [/\bclauses\b/gi, "cls"],
  [/\bclause\b/gi, "cl"],
  [/\bparagraphs\b/gi, "paras"],
  [/\bparagraph\b/gi, "para"],
  [/\barticles\b/gi, "arts"],
  [/\barticle\b/gi, "art"],
  [/\bchapters\b/gi, "chs"],
  [/\bchapter\b/gi, "ch"],
  [/\borders\b/gi, "ords"],
  [/\border\b/gi, "ord"],
];

/** Rule 3.1.4: s, ss, sub-s, sch, pt, div, cl, reg, r — "section 3" is written "s 3". */
export function legislativePinpoint(input: string): string {
  let out = input.trim();
  for (const [pattern, abbreviation] of WORD_ABBREVIATIONS) {
    out = out.replace(pattern, abbreviation);
  }
  return out;
}

const RE_NUMERIC_ISSUE = /^[\d–-]+$/;
const RE_HYPHEN_TAIL = /-(\p{Ll})/gu;

/**
 * Rule 1.7: every word except articles, conjunctions and prepositions takes a capital,
 * and so does the word after a hyphen ("Anti-Discrimination"). The shared title-case
 * function leaves the tail of some hyphenated words lower-case, so this closes that gap.
 */
export function aglcTitleCase(title: string): string {
  return titleCase(title.trim()).replace(
    RE_HYPHEN_TAIL,
    (_match, letter: string) => `-${letter.toUpperCase()}`
  );
}

function ifPresent(text: string, wrap: (value: string) => string): string {
  return text === "" ? "" : wrap(text);
}

/** "Hart", "Edelman and Bant", "Rishworth et al" — rule 4.1.2, for the later footnote. */
function shortAuthors(names: string[], body: boolean): string {
  if (body) {
    return names.join("");
  }
  const surnames = names.map(surnameOf);
  if (surnames.length > 3) {
    return `${surnames[0]} et al`;
  }
  if (surnames.length === 3) {
    return `${surnames[0]}, ${surnames[1]} and ${surnames[2]}`;
  }
  return surnames.join(" and ");
}

/** Rule 4.1.2: two or three authors in full, joined by "and"; more than three, "et al". */
function fullAuthors(names: string[]): string {
  if (names.length > 3) {
    return `${names[0]} et al`;
  }
  if (names.length === 3) {
    return `${names[0]}, ${names[1]} and ${names[2]}`;
  }
  return names.join(" and ");
}

function authorsFrom(values: LegalValues): { names: string[]; body: boolean } {
  const body = get(values, F.body) === "true";
  const raw = get(values, F.authors);
  return { names: body ? [raw] : readAuthorList(raw), body };
}

function footnoteRef(values: LegalValues): string {
  const number = get(values, F.noteNumber);
  return `n ${number || "x"}`;
}

function shortTitleParts(short: string, isItalic: boolean): CitationParts {
  if (short === "") {
    return [];
  }
  return isItalic
    ? [plain(" (‘"), italic(short), plain("’)")]
    : [plain(` (‘${short}’)`)];
}

function entries(
  labels: AglcLabels,
  first: CitationParts,
  later: CitationParts | null,
  bibliography: CitationParts
): CitationOutput["entries"] {
  const out = [{ label: labels.footnote, parts: tidyParts(first) }];
  if (later) {
    out.push({ label: labels.later, parts: tidyParts(later) });
  }
  out.push({ label: labels.ibid, parts: [plain("Ibid.")] });
  out.push({ label: labels.bibliography, parts: tidyParts(bibliography) });
  return out;
}

function output(
  labels: AglcLabels,
  rows: CitationOutput["entries"],
  note: string
): CitationOutput[] {
  return [{ key: "aglc", label: labels.aglc, entries: rows, note }];
}

/* -------------------------------------------------------------------- cases */

/** "175 CLR 1" style citation plus the pinpoint, as rules 2.2.5, 2.3.1 and 2.3.2 set it out. */
interface CaseCitation {
  citation: string;
  pinpoint: string;
  glue: string;
}

function casePinpoint(values: LegalValues, paragraphsOnly: boolean): string {
  const pages = paragraphsOnly ? "" : pageList(get(values, F.pages), aglcSpan);
  const paragraphs = bracketedParagraphs(get(values, F.paragraphs));
  return [pages, paragraphs].filter(Boolean).join(" ");
}

function readCaseCitation(values: LegalValues): CaseCitation | null {
  const report = get(values, F.report);
  const neutral = get(values, F.neutral);
  const unreported = get(values, F.unreported);
  if (report !== "") {
    return {
      citation: report,
      pinpoint: casePinpoint(values, false),
      glue: ", ",
    };
  }
  if (neutral !== "") {
    return {
      citation: neutral,
      pinpoint: casePinpoint(values, true),
      glue: ", ",
    };
  }
  if (unreported !== "") {
    return {
      citation: `(${unreported})`,
      pinpoint: casePinpoint(values, false),
      glue: " ",
    };
  }
  return null;
}

export function aglcCaseIsReady(values: LegalValues): boolean {
  return get(values, F.caseName) !== "" && readCaseCitation(values) !== null;
}

function caseTail(
  values: LegalValues,
  citation: CaseCitation
): { cite: string; judge: string } {
  const pin = citation.pinpoint ? `${citation.glue}${citation.pinpoint}` : "";
  const judge = ifPresent(get(values, F.judge), (j) => ` (${j})`);
  const court = ifPresent(get(values, F.court), (c) => ` (${c})`);
  return { cite: `${citation.citation}${pin}`, judge: `${judge}${court}` };
}

function formatCase(values: LegalValues, labels: AglcLabels): CitationOutput[] {
  const citation = readCaseCitation(values);
  if (!citation) {
    return [];
  }
  const name = get(values, F.caseName);
  const short = get(values, F.shortTitle);
  const { cite, judge } = caseTail(values, citation);
  const inText = get(values, F.inText) === "true";
  const first: CitationParts = [
    ...(inText ? [] : [italic(name), plain(" ")]),
    plain(`${cite}${judge}`),
    ...shortTitleParts(short, true),
    plain("."),
  ];
  const pin = citation.pinpoint ? ` ${citation.pinpoint}` : "";
  const later: CitationParts | null = short
    ? [italic(short), plain(` (${footnoteRef(values)})${pin}${judge}.`)]
    : null;
  const bibliography: CitationParts = [
    italic(name),
    plain(` ${citation.citation}`),
  ];
  const both = get(values, F.report) !== "" && get(values, F.neutral) !== "";
  return output(
    labels,
    entries(labels, first, later, bibliography),
    both ? `${labels.caseNote} ${labels.caseBothNote}` : labels.caseNote
  );
}

/* -------------------------------------------------------------- legislation */

export function aglcLegislationIsReady(values: LegalValues): boolean {
  return get(values, F.title) !== "" && get(values, F.jurisdiction) !== "";
}

function formatLegislation(
  values: LegalValues,
  labels: AglcLabels
): CitationOutput[] {
  const title = get(values, F.title);
  const jurisdiction = get(values, F.jurisdiction);
  const parts = legislativePinpoint(get(values, F.parts));
  const short = get(values, F.shortTitle);
  const pin = parts ? ` ${parts}` : "";
  const first: CitationParts = [
    italic(title),
    plain(` (${jurisdiction})${pin}`),
    ...shortTitleParts(short, true),
    plain("."),
  ];
  const later: CitationParts | null = short
    ? [italic(short), plain(` (${footnoteRef(values)})${pin}.`)]
    : null;
  const bibliography: CitationParts = [
    italic(title),
    plain(` (${jurisdiction})`),
  ];
  return output(
    labels,
    entries(labels, first, later, bibliography),
    labels.legislationNote
  );
}

/* ----------------------------------------------------------- journal & book */

function pinpointText(values: LegalValues): string {
  return shortenSpansIn(get(values, F.pinpoint));
}

function volumeAndIssue(values: LegalValues): string {
  const volume = get(values, F.volume);
  const issue = get(values, F.issue);
  if (issue === "") {
    return volume;
  }
  const numeric = RE_NUMERIC_ISSUE.test(issue);
  if (volume === "") {
    return `(${issue})`;
  }
  return numeric ? `${volume}(${issue})` : `${volume} (${issue})`;
}

function journalYear(values: LegalValues): string {
  const year = get(values, F.year);
  return get(values, F.yearStyle) === "square" ? `[${year}]` : `(${year})`;
}

export function aglcJournalIsReady(values: LegalValues): boolean {
  return (
    get(values, F.authors) !== "" &&
    get(values, F.workTitle) !== "" &&
    get(values, F.year) !== "" &&
    get(values, F.journal) !== "" &&
    get(values, F.startPage) !== ""
  );
}

function formatJournal(
  values: LegalValues,
  labels: AglcLabels
): CitationOutput[] {
  const { names, body } = authorsFrom(values);
  const title = aglcTitleCase(get(values, F.workTitle));
  const volume = volumeAndIssue(values);
  const pin = pinpointText(values);
  const short = get(values, F.shortTitle);
  const start = get(values, F.startPage);
  const lead =
    `${fullAuthors(names)}, ‘${title}’ ${journalYear(values)} ${volume}`.replace(
      "  ",
      " "
    );
  const first: CitationParts = [
    plain(`${lead} `),
    italic(get(values, F.journal)),
    plain(` ${start}${pin ? `, ${pin}` : ""}`),
    ...shortTitleParts(short, false),
    plain("."),
  ];
  const later: CitationParts | null = short
    ? [
        plain(
          `${shortAuthors(names, body)}, ‘${short}’ (${footnoteRef(values)})${pin ? ` ${pin}` : ""}.`
        ),
      ]
    : [
        plain(
          `${shortAuthors(names, body)} (${footnoteRef(values)})${pin ? ` ${pin}` : ""}.`
        ),
      ];
  const inverted = [invertName(names[0]), ...names.slice(1)];
  const bibliography: CitationParts = [
    plain(
      `${body ? names[0] : fullAuthors(inverted)}, ‘${title}’ ${journalYear(values)} ${volume} `
    ),
    italic(get(values, F.journal)),
    plain(` ${start}`),
  ];
  return output(
    labels,
    entries(labels, first, later, bibliography),
    labels.journalNote
  );
}

export function aglcBookIsReady(values: LegalValues): boolean {
  return (
    get(values, F.authors) !== "" &&
    get(values, F.workTitle) !== "" &&
    get(values, F.year) !== ""
  );
}

function bookDetails(values: LegalValues): string {
  const publisher = get(values, F.publisher);
  const edition = get(values, F.edition);
  const year = get(values, F.year);
  return `(${[publisher, edition ? `${edition} ed` : "", year].filter(Boolean).join(", ")})`;
}

function formatBook(values: LegalValues, labels: AglcLabels): CitationOutput[] {
  const { names, body } = authorsFrom(values);
  const title = aglcTitleCase(get(values, F.workTitle));
  const pin = pinpointText(values);
  const short = get(values, F.shortTitle);
  const details = bookDetails(values);
  const first: CitationParts = [
    plain(`${fullAuthors(names)}, `),
    italic(title),
    plain(` ${details}${pin ? ` ${pin}` : ""}`),
    ...shortTitleParts(short, true),
    plain("."),
  ];
  const lead = shortAuthors(names, body);
  const later: CitationParts = short
    ? [
        plain(`${lead}, `),
        italic(short),
        plain(` (${footnoteRef(values)})${pin ? ` ${pin}` : ""}.`),
      ]
    : [plain(`${lead} (${footnoteRef(values)})${pin ? ` ${pin}` : ""}.`)];
  const inverted = [invertName(names[0]), ...names.slice(1)];
  const bibliography: CitationParts = [
    plain(`${body ? names[0] : fullAuthors(inverted)}, `),
    italic(title),
    plain(` ${details}`),
  ];
  return output(
    labels,
    entries(labels, first, later, bibliography),
    labels.bookNote
  );
}

/* ------------------------------------------------------------------- Hansard */

export function aglcHansardIsReady(values: LegalValues): boolean {
  return (
    get(values, F.hJurisdiction) !== "" &&
    get(values, F.chamber) !== "" &&
    parsePartialDate(get(values, F.date)) !== null
  );
}

function formatHansard(
  values: LegalValues,
  labels: AglcLabels
): CitationOutput[] {
  const jurisdiction = get(values, F.hJurisdiction);
  const chamber = get(values, F.chamber);
  const date = dayMonthYear(parsePartialDate(get(values, F.date)));
  const pin = pinpointText(values);
  const speaker = ifPresent(get(values, F.speaker), (s) => ` (${s})`);
  const head: CitationParts = [
    plain(`${jurisdiction}, `),
    italic("Parliamentary Debates"),
    plain(`, ${chamber}, ${date}`),
  ];
  const first: CitationParts = [
    ...head,
    plain(`${pin ? `, ${pin}` : ""}${speaker}.`),
  ];
  const bibliography: CitationParts = [...head];
  return output(
    labels,
    entries(labels, first, null, bibliography),
    labels.hansardNote
  );
}

/* ------------------------------------------------------------------- public */

export function aglcIsReady(kind: AglcKind, values: LegalValues): boolean {
  switch (kind) {
    case "case":
      return aglcCaseIsReady(values);
    case "legislation":
      return aglcLegislationIsReady(values);
    case "journal":
      return aglcJournalIsReady(values);
    case "book":
      return aglcBookIsReady(values);
    default:
      return aglcHansardIsReady(values);
  }
}

export function formatAglc(
  kind: AglcKind,
  values: LegalValues,
  labels: AglcLabels
): CitationOutput[] {
  switch (kind) {
    case "case":
      return formatCase(values, labels);
    case "legislation":
      return formatLegislation(values, labels);
    case "journal":
      return formatJournal(values, labels);
    case "book":
      return formatBook(values, labels);
    default:
      return formatHansard(values, labels);
  }
}
